import { TbBrain, TbCheck } from "solid-icons/tb";
import { type Component, Show } from "solid-js";
import type { LiveSummaryTag } from "./types";

/**
 * The marker in front of a tag's name: a plain dot, and a brain where the tags
 * manager says a tag is Smart.
 *
 * Which of the two a tag is stays a tags-manager fact. A chip is read at a
 * glance, in rows of them, and whether a tag applied itself or somebody applied
 * it is not what the reader is scanning for there — marking every chip only
 * costs the row legibility. Drawn in currentColor so it follows the chip through
 * both themes and the selected fill without knowing about any of them. The
 * brain's stroke is heavier than a Tabler icon's default: at the dot's own size
 * a default stroke antialiases into a smudge rather than reading as a brain.
 */
export const TagGlyph: Component<{ smart?: boolean }> = (props) => (
  <Show
    when={props.smart}
    fallback={
      <svg viewBox="0 0 10 10" class="size-[calc(0.625rem*var(--font-scale,1))] shrink-0" aria-hidden="true">
        <circle cx="5" cy="5" r="3" fill="currentColor" />
      </svg>
    }
  >
    <TbBrain class="size-[calc(0.625rem*var(--font-scale,1))] shrink-0" stroke-width={3.5} aria-hidden="true" />
  </Show>
);

/**
 * The one tag renderer. Header pills, the chips on an item, the tags manager,
 * the rail under a summary row and the capture bubble's quick row all come
 * through here, so a tag looks the same wherever it appears.
 *
 * The tag's own colour is handed to the stylesheet as a custom property and
 * live-summary.css derives the ink from it per theme: the palette is picked to
 * read as swatches, and text set in it does not clear WCAG AA on the surfaces
 * these are drawn on. So a chip carries no knowledge of the current theme and
 * follows a change of theme with no re-render.
 */
export const TagChip: Component<{
  tag: LiveSummaryTag;
  onClick?: (event: MouseEvent) => void;
  onRemove?: () => void;
  // selected fills the chip rather than tinting it, for the header pill's
  // pressed state.
  selected?: boolean;
  // How much the tag holds. Printed rather than left to the title, which the
  // neighbouring kind pills already do and which a tooltip cannot be scanned for.
  count?: number;
  // bead is the tighter form the rail under a summary row uses, where the name
  // truncates rather than wrapping so one tag stays one bead.
  bead?: boolean;
  // auto marks a tag Chief applied rather than a person, drawn dashed.
  auto?: boolean;
  title?: string;
  // The chip's accessible name when its name alone isn't all a reader needs.
  label?: string;
}> = (props) => {
  // A chip given no handler at all is a label, and a button that does nothing on
  // click should not take a pointer or the focus ring as though it will.
  const interactive = () => Boolean(props.onClick || props.onRemove);

  return (
    <button
      type="button"
      disabled={!interactive()}
      aria-pressed={props.selected}
      title={props.title ?? props.tag.name}
      aria-label={props.label}
      data-selected={props.selected ? "true" : undefined}
      data-auto={props.auto ? "true" : undefined}
      onClick={(e) => {
        e.stopPropagation();
        if (props.onRemove) props.onRemove();
        else props.onClick?.(e);
      }}
      class="lsum-chip inline-flex shrink-0 items-center gap-1 rounded-md border text-[calc(0.6875rem*var(--font-scale,1))] leading-none enabled:cursor-pointer hover:enabled:opacity-80"
      classList={{ "font-semibold": props.selected, "lsum-bead": props.bead, "px-2 py-0.75": !props.bead }}
      style={{ "--chip-raw": props.tag.color }}
    >
      <Show when={props.selected} fallback={<TagGlyph />}>
        <TbCheck class="size-[calc(0.625rem*var(--font-scale,1))] shrink-0" aria-hidden="true" />
      </Show>
      <Show when={props.bead} fallback={props.tag.name}>
        <span class="lsum-bead-name">{props.tag.name}</span>
      </Show>
      {/* Mono and tabular so counts down a row of chips line up, which is the
          one thing in a chip that is a number rather than a name. */}
      <Show when={props.count !== undefined}>
        <span class="lsum-chip-n font-mono tabular-nums">{props.count}</span>
      </Show>
      <Show when={props.onRemove}>
        <span aria-hidden="true">×</span>
      </Show>
    </button>
  );
};

/**
 * The filter row collapsed to the one tag it's narrowed to, plus the way out.
 * Shared by the summary and Chief's timeline so a tag filter reads and clears
 * the same way in both.
 */
export const ActiveTagFilter: Component<{
  tag: LiveSummaryTag;
  onClear: () => void;
}> = (props) => (
  <div class="flex min-w-0 items-center gap-2">
    <TagChip tag={props.tag} selected title={`Clear the ${props.tag.name} filter`} onClick={props.onClear} />
    <button
      type="button"
      onClick={props.onClear}
      class="shrink-0 cursor-pointer rounded-md border border-primary-border px-2 py-0.5 text-[calc(0.6875rem*var(--font-scale,1))] text-secondary-text hover:text-primary-text"
    >
      Clear ×
    </button>
  </div>
);
